import { FIELD_LABELS, NEW_EVENT_ID_PREFIX } from './constants'
import type {
  AnalyticsEvent,
  DiffChange,
  DiffChangeType,
  EventsTableFilters,
  GlobalProperty,
  PropertiesSchema,
  PublishPreviewSnapshot,
} from './types'

import type { AnalyticsLifecycleState, AnalyticsPlatform } from '@/api/generated/api'

type PendingEventChange = {
  name?: string
  description?: string | null
  trigger?: string | null
  platforms?: AnalyticsPlatform[]
  lifecycle_state?: AnalyticsLifecycleState
  properties_schema?: object
}

type ExistingEvent = {
  id: string
  name: string
  description?: string | null
  trigger?: string | null
  platforms?: AnalyticsPlatform[]
  lifecycle_state?: AnalyticsLifecycleState
  properties_schema?: object
}

type GlobalPropsEventInput = { properties_schema?: object } | null

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return '(empty)'
  if (Array.isArray(value)) return value.join(', ') || '(none)'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

const normalizeProperty = (prop: unknown): GlobalProperty => {
  if (typeof prop === 'string') {
    return { description: prop, lifecycle_state: 'draft' }
  }
  if (typeof prop === 'object' && prop !== null) {
    const p = prop as Record<string, unknown>
    let lifecycle_state: AnalyticsLifecycleState = (p.lifecycle_state as AnalyticsLifecycleState) || 'draft'
    if (p.deprecated === true && !p.lifecycle_state) {
      lifecycle_state = 'deprecated'
    }
    return {
      description: (p.description as string) || '',
      lifecycle_state,
    }
  }
  return { description: '', lifecycle_state: 'draft' }
}

export const mergeEventsWithPendingChanges = (
  events: AnalyticsEvent[],
  pendingChanges: Map<string, Partial<AnalyticsEvent>>
): AnalyticsEvent[] => {
  const mergedEvents = events.map((event) => {
    const pendingChange = pendingChanges.get(event.id)
    if (pendingChange) {
      const merged: AnalyticsEvent = { ...event }
      if (pendingChange.name !== undefined) merged.name = pendingChange.name
      if (pendingChange.description !== undefined) merged.description = pendingChange.description
      if (pendingChange.trigger !== undefined) merged.trigger = pendingChange.trigger
      if (pendingChange.properties_schema !== undefined) merged.properties_schema = pendingChange.properties_schema
      if (pendingChange.platforms !== undefined) merged.platforms = pendingChange.platforms
      if (pendingChange.lifecycle_state !== undefined) merged.lifecycle_state = pendingChange.lifecycle_state
      return merged
    }
    return event
  })

  const newEvents: AnalyticsEvent[] = []
  pendingChanges.forEach((change, id) => {
    if (id.startsWith(NEW_EVENT_ID_PREFIX)) {
      newEvents.push(change as AnalyticsEvent)
    }
  })

  return [...mergedEvents, ...newEvents]
}

export const filterEventsBySearch = (events: AnalyticsEvent[], searchQuery: string): AnalyticsEvent[] => {
  const trimmed = searchQuery.trim()
  if (!trimmed) return events
  const query = trimmed.toLowerCase()
  return events.filter((event) => event.name?.toLowerCase().includes(query))
}

export const filterEventsByFilters = (events: AnalyticsEvent[], filters: EventsTableFilters): AnalyticsEvent[] => {
  let result = events
  if (filters.lifecycle.length > 0) {
    result = result.filter((event) => filters.lifecycle.includes(event.lifecycle_state ?? 'draft'))
  }
  if (filters.status.length > 0) {
    result = result.filter((event) => filters.status.includes(event.status))
  }
  if (filters.platform.length > 0) {
    result = result.filter((event) => event.platforms?.some((p) => filters.platform.includes(p)))
  }
  return result
}

export const getButtonTitle = (isLoading: boolean, loadingText: string, defaultText: string): string => {
  return isLoading ? loadingText : defaultText
}

export const getReviewButtonTitle = (isSaving: boolean): string =>
  getButtonTitle(isSaving, 'Saving...', 'Review Changes')

const getSchema = (globalPropsEvent: GlobalPropsEventInput): PropertiesSchema => {
  return (globalPropsEvent?.properties_schema as PropertiesSchema) ?? {}
}

const VALID_DIFF_CHANGE_TYPES: DiffChangeType[] = ['new', 'updated', 'deprecated', 'renamed']

const isDiffChangeType = (value: string): value is DiffChangeType => {
  return VALID_DIFF_CHANGE_TYPES.includes(value as DiffChangeType)
}

const computeEventDiff = (
  pendingChanges: Map<string, PendingEventChange>,
  existingEvents: ExistingEvent[]
): DiffChange[] => {
  const eventChanges: DiffChange[] = []
  pendingChanges.forEach((event, id) => {
    if (id.startsWith('new-')) {
      eventChanges.push({
        id,
        name: event.name || '(unnamed)',
        type: 'new',
        description: event.description || undefined,
        trigger: event.trigger || undefined,
        platforms: event.platforms,
        lifecycle: event.lifecycle_state,
      })
    } else {
      const orig = existingEvents.find((e) => e.id === id)
      if (!orig) return
      const origRecord = orig as Record<string, unknown>
      const eventRecord = event as Record<string, unknown>
      const fields = Object.keys(FIELD_LABELS)
        .filter((f) => JSON.stringify(origRecord[f]) !== JSON.stringify(eventRecord[f]) && eventRecord[f] !== undefined)
        .map((f) => ({ field: f, oldVal: formatValue(origRecord[f]), newVal: formatValue(eventRecord[f]) }))
      if (fields.length) {
        eventChanges.push({ id, name: event.name || orig.name || '(unnamed)', type: 'updated', fields })
      }
    }
  })
  return eventChanges
}

const computePropertyDiff = (
  pendingGlobalProps: Record<string, GlobalProperty>,
  globalPropsEvent: GlobalPropsEventInput
): DiffChange[] => {
  const propertyChanges: DiffChange[] = []

  const origSchema: Record<string, GlobalProperty> = {}
  const newSchema: Record<string, GlobalProperty> = {}
  Object.entries(getSchema(globalPropsEvent)).forEach(([n, p]) => {
    origSchema[n] = normalizeProperty(p)
  })
  Object.entries(pendingGlobalProps).forEach(([n, p]) => {
    newSchema[n] = normalizeProperty(p)
  })

  const adds: { name: string; prop: GlobalProperty }[] = []
  const removes: { name: string; prop: GlobalProperty }[] = []

  Object.entries(newSchema).forEach(([name, prop]) => {
    if (!origSchema[name]) {
      adds.push({ name, prop })
    } else if (JSON.stringify(origSchema[name]) !== JSON.stringify(prop)) {
      propertyChanges.push({
        id: name,
        name,
        type: 'updated',
        fields: [{ field: 'Description', oldVal: origSchema[name].description, newVal: prop.description }],
      })
    }
  })

  Object.entries(origSchema).forEach(([name, prop]) => {
    if (!newSchema[name]) {
      removes.push({ name, prop })
    }
  })

  const matchedAdds = new Set<number>()
  removes.forEach((rem) => {
    const matchIdx = adds.findIndex(
      (a, i) => !matchedAdds.has(i) && a.prop.description === rem.prop.description && a.prop.description
    )
    if (matchIdx !== -1) {
      matchedAdds.add(matchIdx)
      propertyChanges.push({
        id: adds[matchIdx].name,
        name: adds[matchIdx].name,
        type: 'renamed',
        fields: [{ field: 'Name', oldVal: rem.name, newVal: adds[matchIdx].name }],
      })
    } else {
      propertyChanges.push({ id: rem.name, name: rem.name, type: 'deprecated' })
    }
  })

  adds.forEach((a, i) => {
    if (!matchedAdds.has(i)) {
      propertyChanges.push({
        id: a.name,
        name: a.name,
        type: 'new',
        description: a.prop.description,
        lifecycle: a.prop.lifecycle_state,
      })
    }
  })

  return propertyChanges
}

export const computeSaveDiff = (
  pendingChanges: Map<string, PendingEventChange>,
  existingEvents: ExistingEvent[],
  pendingGlobalProps: Record<string, GlobalProperty> | null,
  globalPropsEvent: GlobalPropsEventInput
): { eventChanges: DiffChange[]; propertyChanges: DiffChange[] } => {
  const eventChanges = computeEventDiff(pendingChanges, existingEvents)
  const propertyChanges = pendingGlobalProps ? computePropertyDiff(pendingGlobalProps, globalPropsEvent) : []
  return { eventChanges, propertyChanges }
}

export const computePublishDiff = (
  snapshot: PublishPreviewSnapshot,
  globalPropertiesEvent: GlobalPropsEventInput
): { eventChanges: DiffChange[]; propertyChanges: DiffChange[] } => {
  const eventChanges: DiffChange[] = []

  for (const event of snapshot.events) {
    if (event.change_type === 'unchanged') continue
    if (!isDiffChangeType(event.change_type)) continue

    const change: DiffChange = {
      id: event.id,
      name: event.name,
      type: event.change_type,
      description: event.description || undefined,
      trigger: event.trigger || undefined,
      platforms: event.platforms,
      lifecycle: event.lifecycle_state,
    }

    eventChanges.push(change)
  }

  const propertyChanges: DiffChange[] = []
  const schema = getSchema(globalPropertiesEvent)

  Object.entries(schema).forEach(([name, rawProp]) => {
    const prop = normalizeProperty(rawProp)
    const typedProp = rawProp as GlobalProperty
    const changeType = typedProp?.change_type
    const status = typedProp?.status

    // use change_type from backend if available, otherwise fall back to status check
    if (changeType && changeType !== 'unchanged' && isDiffChangeType(changeType)) {
      propertyChanges.push({
        id: name,
        name,
        type: changeType,
        description: prop.description,
        lifecycle: prop.lifecycle_state,
      })
    } else if (status === 'needs_publish') {
      propertyChanges.push({
        id: name,
        name,
        type: 'updated',
        description: prop.description,
        lifecycle: prop.lifecycle_state,
      })
    }
  })

  return { eventChanges, propertyChanges }
}
